import { useState, useEffect, FormEvent } from "react";
import getComponentsFromApi from "@services/RocketComponent/getRocketComponents";
import { INITIAL_DEFAULT_LIMIT, INITIAL_DEFAULT_OFFSET, INITIAL_DEFAULT_PAGE } from "@constants/pagination";
import { IComponent } from "@interfaces/RocketComponent/Component";

interface returnTypes {
    isLoading: boolean;
    components: IComponent[] | undefined;
    status?: string;
    handleGetComponents: () => Promise<void>;
    handleSearchComponents: (event: FormEvent) => void;
    resetFilters: () => void;
    filterByStatus: (statusClick: string) => void;
    handleRocketPartClicked: (brandClick: string, typeClick: string) => void;
    hasMore: boolean;
}

const useGetRocketComponents = (): returnTypes => {
    const [isLoading, setIsLoading] = useState(true);
    const [components, setComponents] = useState<IComponent[]>([]);
    const [offset, setOffset] = useState(INITIAL_DEFAULT_OFFSET);
    const [hasMore, setHasMore] = useState(true);
    const [searchByName, setSearchByName] = useState<string>("");
    const [page, setPage] = useState(INITIAL_DEFAULT_PAGE);
    const [status, setStatus] = useState<string>();
    const [brand, setBrand] = useState<string>();
    const [type, setType] = useState<string>();

    useEffect(() => {
        handleGetComponents();
    }, [status, brand, type, searchByName]);

    const getComponents = async (): Promise<IComponent[]> => {
        setIsLoading(true);
        let componentsList: IComponent[] = [];

        await getComponentsFromApi(page, offset, status, brand, type, searchByName)
            .then(({ data }) => {
                componentsList = data;
            })
            .catch((error) => console.log(error))
            .finally(() => setIsLoading(false));

        return componentsList;
    };

    const handleGetComponents = async () => {
        const componentsList = await getComponents();

        setOffset(INITIAL_DEFAULT_LIMIT);
        setHasMore(componentsList.length !== 0 && componentsList.length >= INITIAL_DEFAULT_LIMIT);
        setComponents(components.concat(componentsList));
        setPage((page) => page + 1);
    };

    const handleRocketPartClicked = (brandClick: string, typeClick: string) => {
        if (brandClick !== brand || typeClick !== type) {
            resetFilters();
            setBrand(brandClick);
            setType(typeClick);
        }
    };

    const handleSearchComponents = (event: FormEvent) => {
        event.preventDefault();
        setPage(INITIAL_DEFAULT_PAGE);
        setOffset(INITIAL_DEFAULT_OFFSET);
        setComponents([]);
        setBrand(undefined);
        setType(undefined);
        setSearchByName(event.target[0].value);
    };

    const resetFilters = () => {
        setPage(0);
        setComponents([]);
        setStatus(undefined);
        setBrand(undefined);
        setType(undefined);
    };

    const filterByStatus = (statusClick: string): void => {
        setPage(0);
        setComponents([]);
        setStatus(statusClick);
    };

    return {
        isLoading,
        components,
        status,
        handleGetComponents,
        handleSearchComponents,
        resetFilters,
        filterByStatus,
        handleRocketPartClicked,
        hasMore,
    };
};

export default useGetRocketComponents;
